// Kanban — FE-PINV-070.
//
// Horizontal column-per-stage view of the same Renewal Board payload
// the Grid renders. Re-groups by stage instead of (stage × month).
// Closed Won / Closed Lost surface only when `show_closed` is true
// (lifted from the toolbar by FE-PINV-063's shell).
//
// FE-PINV-FOCUS-STAGE-UI piggybacks on this component by reading
// `board.today_focus_stage` (an optional response field BE-PINV-
// FOCUS-STAGE adds) and applying `data-focus-stage="true"` plus the
// "TODAY'S FOCUS STAGE" pill to the matching column container.

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { KanbanCard } from "./KanbanCard";
import type {
  OpportunityStage,
  RenewalBoardOpportunity,
  RenewalBoardResponse,
} from "./types";


const ACTIVE_STAGES: OpportunityStage[] = [
  "lead",
  "meeting_set",
  "qualified",
  "proposal",
  "negotiation",
  "bor_signed_pending_effective_date",
];


const STAGE_LABEL: Record<OpportunityStage, string> = {
  lead: "Lead",
  meeting_set: "Meeting set",
  qualified: "Qualified",
  proposal: "Proposal",
  negotiation: "Negotiation",
  bor_signed_pending_effective_date: "BOR signed",
  closed_won: "Closed won",
  closed_lost: "Closed lost",
};


const DEFAULT_VISIBLE_PER_COLUMN = 7;


// FE-PINV-FOCUS-STAGE-UI — narrow-viewport threshold. Below this the
// "TODAY'S FOCUS STAGE" pill collapses to a dot so it doesn't overlap
// neighboring columns. The 3px focus border stays at every width.
const FOCUS_PILL_COLLAPSE_BELOW_PX = 640;


type BoardWithFocus = RenewalBoardResponse & {
  today_focus_stage?: OpportunityStage | string | null;
};


export interface KanbanDropEvent {
  opportunity_id: string;
  from_stage: string;
  to_stage: string;
}


export interface KanbanProps {
  board: BoardWithFocus;
  show_closed: boolean;
  onDrop: (event: KanbanDropEvent) => void;
}


function regroupByStage(
  board: RenewalBoardResponse,
): Record<OpportunityStage, RenewalBoardOpportunity[]> {
  const out: Record<OpportunityStage, RenewalBoardOpportunity[]> = {
    lead: [], meeting_set: [], qualified: [],
    proposal: [], negotiation: [], bor_signed_pending_effective_date: [],
    closed_won: [], closed_lost: [],
  };
  for (const col of board.columns) {
    for (const cell of col.cells) {
      const bucket = out[cell.stage as OpportunityStage];
      if (!bucket) continue;
      for (const opp of cell.opportunities) {
        bucket.push(opp);
      }
    }
  }
  for (const opp of board.summary_row.closed_won.opportunities) {
    out.closed_won.push(opp);
  }
  for (const opp of board.summary_row.closed_lost.opportunities) {
    out.closed_lost.push(opp);
  }
  for (const stage of Object.keys(out) as OpportunityStage[]) {
    out[stage].sort((a, b) => a.days_until_renewal - b.days_until_renewal);
  }
  return out;
}


export function Kanban({
  board, show_closed, onDrop,
}: KanbanProps): JSX.Element {
  const navigate = useNavigate();
  const grouped = regroupByStage(board);
  const columns: OpportunityStage[] = show_closed
    ? [...ACTIVE_STAGES, "closed_won", "closed_lost"]
    : ACTIVE_STAGES;

  const focus_raw = board.today_focus_stage;
  const focus_stage = (
    focus_raw && (ACTIVE_STAGES as string[]).includes(focus_raw)
  ) ? focus_raw as OpportunityStage : null;

  return (
    <div
      data-testid="renewal-board-kanban"
      className="flex gap-3 overflow-x-auto pb-2"
    >
      {columns.map((stage) => (
        <KanbanColumn
          key={stage}
          stage={stage}
          opps={grouped[stage]}
          is_focus_stage={focus_stage === stage}
          onDrop={(opportunity_id, from_stage) => {
            if (from_stage === stage) return;
            onDrop({
              opportunity_id, from_stage, to_stage: stage,
            });
          }}
          onCardClick={(id) =>
            navigate(`/dashboard/sales-crm/opportunities/${id}`)
          }
        />
      ))}
    </div>
  );
}


function KanbanColumn({
  stage, opps, is_focus_stage, onDrop, onCardClick,
}: {
  stage: OpportunityStage;
  opps: RenewalBoardOpportunity[];
  is_focus_stage: boolean;
  onDrop: (opportunity_id: string, from_stage: string) => void;
  onCardClick: (id: string) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const [narrow, setNarrow] = useState(false);
  const overflow = opps.length - DEFAULT_VISIBLE_PER_COLUMN;
  const visible = expanded
    ? opps
    : opps.slice(0, DEFAULT_VISIBLE_PER_COLUMN);
  const ref = useRef<HTMLElement>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (typeof ResizeObserver === "undefined") return;
    const target = ref.current;
    if (!target) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect?.width
          ?? (entry.target as HTMLElement).clientWidth;
        setNarrow(width > 0 && width < FOCUS_PILL_COLLAPSE_BELOW_PX);
      }
    });
    ro.observe(target);
    return () => ro.disconnect();
  }, []);

  return (
    <section
      ref={ref}
      data-testid={`kanban-column-${stage}`}
      data-stage={stage}
      data-focus-stage={is_focus_stage ? "true" : undefined}
      aria-label={
        is_focus_stage
          ? `Today's focus stage: ${STAGE_LABEL[stage]}`
          : STAGE_LABEL[stage]
      }
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDrop={(e) => {
        e.preventDefault();
        const raw = e.dataTransfer.getData(
          "application/x-renewal-board-opp",
        );
        if (!raw) return;
        try {
          const parsed = JSON.parse(raw);
          if (typeof parsed?.opportunity_id !== "string") return;
          onDrop(parsed.opportunity_id, parsed.stage);
        } catch {
          // Bad payload — ignore, same as Grid's drop handler.
        }
      }}
      style={
        is_focus_stage
          // 3px forest-deep border (sage at -20% lightness ~ #566E66).
          // Scoped to this surface — no new token introduced.
          ? { boxShadow: "0 0 0 3px #566E66 inset" }
          : undefined
      }
      className={[
        "relative flex w-72 shrink-0 flex-col gap-2 rounded-md",
        "bg-canvas p-2 min-h-[120px]",
      ].join(" ")}
    >
      {is_focus_stage && (narrow ? (
        <span
          data-testid={`kanban-focus-pill-${stage}-dot`}
          aria-hidden="true"
          title="Today's focus stage"
          style={{ backgroundColor: "#566E66" }}
          className={[
            "absolute -top-1.5 left-1/2 -translate-x-1/2",
            "h-2 w-2 rounded-full",
          ].join(" ")}
        />
      ) : (
        <span
          data-testid={`kanban-focus-pill-${stage}`}
          style={{
            backgroundColor: "#566E66",
            color: "#FFFFFF",
            letterSpacing: "1.2px",
            paddingTop: 2,
            paddingBottom: 2,
            paddingLeft: 8,
            paddingRight: 8,
            borderRadius: 10,
            fontSize: 10,
          }}
          className={[
            "absolute -top-2 left-1/2 -translate-x-1/2",
            "font-mono uppercase whitespace-nowrap",
          ].join(" ")}
        >
          Today's focus stage
        </span>
      ))}
      <header className="flex items-baseline justify-between px-1">
        <h3 className="text-[11px] font-semibold uppercase tracking-wide text-text-secondary">
          {STAGE_LABEL[stage]}
        </h3>
        <span
          data-testid={`kanban-column-count-${stage}`}
          className="text-[11px] tabular-nums text-text-tertiary"
        >
          {opps.length}
        </span>
      </header>

      {opps.length === 0 ? (
        <div
          data-testid={`kanban-empty-${stage}`}
          className="rounded-md border border-dashed border-border-subtle p-3 text-center text-[11px] italic text-text-tertiary"
        >
          No opportunities in {STAGE_LABEL[stage]}
        </div>
      ) : (
        <>
          <div className="flex flex-col gap-1.5">
            {visible.map((opp) => (
              <KanbanCard
                key={opp.opportunity_id}
                opp={opp}
                onClick={onCardClick}
              />
            ))}
          </div>
          {overflow > 0 && !expanded && (
            <button
              type="button"
              data-testid={`kanban-show-more-${stage}`}
              onClick={() => setExpanded(true)}
              className={[
                "mt-1 self-center rounded-md px-2 py-1 text-[11px]",
                "text-text-secondary hover:bg-recessed hover:text-text-primary",
              ].join(" ")}
            >
              Show {overflow} more
            </button>
          )}
        </>
      )}
    </section>
  );
}
